import Immutable from 'immutable';
import React from 'react';
import Flux from 'lib/flux';
import BookingRequestConstants from 'constants/booking_request';

const BookingRequestStore = Flux.createStore({
  name: 'booking_request',

  getInitialState() {
    return {
      loading: false,
      loadingSummary: false,
      discountLoading: false,
      discount_applied: false,
      errors: {},
      saved: false,
      committed: false,
      message: null,
      discount_code: null,
      units: [],
      addons: [],
      guests: 0,
      time: null,
      date: null,
      summary_props: {},
      fully_discounted: false,
      tour_id: null,
      available_addons: [],
      paid_in_cash: null,
      requiresAuth: false,
      authCompleted: false,
      customerSecret: null,
      stripeCardElement: null,
      stripeToken: null,
      stripePaymentError: null,
      paymentId: null,
      managedAccountToken: null,
      createdAt: null,
    };
  },

  actions: [
    [
      BookingRequestConstants.CHANGE,
      function (fields) {
        this.state = this.state.merge({
          units: fields.units,
          guests: fields.units.reduce((t, unit) => t + unit.count, 0),
          addons: fields.addons,
          time: fields.time,
          date: fields.date,
        });
      },
    ],

    [
      BookingRequestConstants.SAVE,
      function (resp) {
        if (this.state.get('loading') === true) {
          return;
        }
        this.state = this.state.merge({
          loading: true,
          saved: false,
          errors: {},
        });
      },
    ],

    [
      BookingRequestConstants.SAVE_COMPLETED,
      function (params) {
        this.state = this.state.merge({
          loading: false,
          saved: true,
          errors: {},
          booking_request_id: params.id,
          paid_in_cash: params.paid_in_cash,
          managedAccountToken: params.managedAccountToken,
          createdAt: params.createdAt,
          isOutsidePayLaterCutoff: params.isOutsidePayLaterCutoff,
          willExpireOn: params.willExpireOn,
        });
      },
    ],

    [
      BookingRequestConstants.SAVE_FAILED,
      function (json) {
        const errors = json.errors || {};

        // Send timeslot error to the booking details page with timestamp
        if (json.message === 'Ticket capacity error') {
          errors['ticket-capacity'] =
            `${json.message} (${new Date().toISOString()})`;
        }

        if (json.message === 'Ticket capacity job in progress') {
          errors['ticket-capacity-job-in-progress'] =
            `${json.message} (${new Date().toISOString()})`;
        }

        this.state = this.state.merge({
          loading: false,
          saved: false,
          message: json.message,
          errors,
        });
      },
    ],

    [
      BookingRequestConstants.COMMIT,
      function () {
        if (this.state.get('loading') === true) {
          return;
        }
        this.state = this.state.merge({
          loading: true,
          committed: false,
          errors: {},
        });
      },
    ],

    [
      BookingRequestConstants.COMMIT_COMPLETED,
      function (params) {
        let loading;
        const requiresAuth = !!params.actionRequired;
        const customerSecret = requiresAuth ? params.customerSecret : null;
        const committed = params.committedAt ? true : null;
        this.state = this.state.merge({
          loading,
          requiresAuth,
          customerSecret,
          committed,
          errors: {},
          paymentId: params.paymentId,
          paidInFull: params.paidInFull,
        });
      },
    ],

    [
      BookingRequestConstants.AUTH_COMPLETED,
      function (params) {
        this.state = this.state.merge({
          loading: false,
          authCompleted: true,
          committed: true,
        });
      },
    ],

    [
      BookingRequestConstants.SET_STRIPE_TOKEN,
      function (token) {
        this.state = this.state.merge({
          stripeToken: token,
        });
      },
    ],

    [
      BookingRequestConstants.ADD_STRIPE_CARD_ELEMENT,
      function (card) {
        this.state = this.state.merge({
          stripeCardElement: card,
        });
      },
    ],

    [
      BookingRequestConstants.STRIPE_PAYMENT_ERROR,
      function (error) {
        this.state = this.state.merge({
          loading: false,
          committed: false,
          message: 'Payment failed',
          requiresAuth: false,
          errors: { stripePaymentError: error },
        });
      },
    ],

    [
      BookingRequestConstants.RESET_STRIPE_AUTH,
      function () {
        this.state = this.state.merge({
          loading: false,
          committed: false,
          authCompleted: false,
          requiresAuth: false,
          errors: {},
        });
      },
    ],

    [
      BookingRequestConstants.COMMIT_FAILED,
      function (json) {
        const errors = json.errors || {};

        this.state = this.state.merge({
          loading: false,
          committed: false,
          message: json.message,
          errors,
        });
      },
    ],

    [
      BookingRequestConstants.APPLY_DISCOUNT,
      function () {
        if (this.state.get('loading') === true) {
          return;
        }
        this.state = this.state.merge({
          discountLoading: true,
          discount_applied: false,
          discount_code: null,
          errors: {},
        });
      },
    ],

    [
      BookingRequestConstants.APPLY_DISCOUNT_COMPLETED,
      function (params) {
        this.state = this.state.merge({
          discountLoading: false,
          discount_applied: true,
          fully_discounted: params.fully_discounted,
          discount_code: params.discount_code,
          errors: {},
        });
      },
    ],

    [
      BookingRequestConstants.APPLY_DISCOUNT_FAILED,
      function (json) {
        const errors = json.errors || {};

        this.state = this.state.merge({
          discountLoading: false,
          discount_applied: false,
          message: json.message,
          discount_code: null,
          errors,
        });
      },
    ],

    [
      BookingRequestConstants.REMOVE_DISCOUNT,
      function () {
        if (this.state.get('loading') === true) {
          return;
        }
        this.state = this.state.merge({
          discountLoading: true,
          errors: {},
        });
      },
    ],

    [
      BookingRequestConstants.REMOVE_DISCOUNT_COMPLETED,
      function (params) {
        this.state = this.state.merge({
          discountLoading: false,
          discount_applied: false,
          discount_code: null,
          fully_discounted: params.fully_discounted,
          errors: {},
        });
      },
    ],

    [
      BookingRequestConstants.REMOVE_DISCOUNT_FAILED,
      function (json) {
        const errors = json.errors || {};

        this.state = this.state.merge({
          discountLoading: false,
          discount_applied: true,
          message: json.message,
          errors,
        });
      },
    ],

    [
      BookingRequestConstants.FETCH_SUMMARY,
      function () {
        this.state = this.state.set('loadingSummary', true);
      },
    ],

    [
      BookingRequestConstants.FETCH_SUMMARY_COMPLETED,
      function (json) {
        this.state = this.state.merge({
          loadingSummary: false,
          summary_props: json.summary_props,
        });
      },
    ],

    [
      BookingRequestConstants.FETCH_SUMMARY_FAILED,
      function () {
        this.state = this.state.set('loading', false);
      },
    ],
  ],
});

export default BookingRequestStore;
